import { createClient } from '@/lib/supabase/client';

type EmailType = 'password_reset' | 'invite_code' | 'design_notification' | 'artist_update' | 'inquiry_received' | 'inquiry_response' | 'order_confirmation';

interface EmailPayload {
  type: EmailType;
  to: string | string[];
  data?: Record<string, any>;
}

async function invokeEmailFunction(payload: EmailPayload): Promise<{ success: boolean; error?: string }> {
  try {
    const supabase = createClient();
    const { data, error } = await supabase.functions.invoke('send-email', {
      body: payload,
    });
    if (error) throw error;
    return { success: true };
  } catch (err: any) {
    console.error('[TryInk Email]', err?.message ?? err);
    return { success: false, error: err?.message ?? 'Email send failed' };
  }
}

/** Send a password reset email */
export async function sendPasswordResetEmail(to: string, resetLink: string, userName?: string) {
  return invokeEmailFunction({
    type: 'password_reset',
    to,
    data: { resetLink, userName },
  });
}

/** Send an invite code email */
export async function sendInviteCodeEmail(
  to: string,
  inviteCode: string,
  role: 'user' | 'artist' = 'user',
  senderName?: string
) {
  return invokeEmailFunction({
    type: 'invite_code',
    to,
    data: { inviteCode, role, senderName },
  });
}

/** Send a design upload notification to followers/wishlist users */
export async function sendDesignNotificationEmail(
  to: string | string[],
  designName: string,
  artistName: string,
  designUrl: string,
  thumbnailUrl?: string
) {
  return invokeEmailFunction({
    type: 'design_notification',
    to,
    data: { designName, artistName, designUrl, thumbnailUrl },
  });
}

/** Send an artist update to followers */
export async function sendArtistUpdateEmail(
  to: string | string[],
  artistName: string,
  artistProfileUrl: string,
  updateTitle?: string,
  updateBody?: string
) {
  return invokeEmailFunction({
    type: 'artist_update',
    to,
    data: { artistName, artistProfileUrl, updateTitle, updateBody },
  });
}

/** Send notification to artist when a new inquiry is received */
export async function sendInquiryReceivedEmail(
  to: string,
  artistName: string,
  senderName: string,
  subject: string,
  inquiryType: string,
  dashboardUrl: string
) {
  return invokeEmailFunction({
    type: 'inquiry_received',
    to,
    data: { artistName, senderName, subject, inquiryType, dashboardUrl },
  });
}

/** Send notification to user when artist responds to their inquiry */
export async function sendInquiryResponseEmail(
  to: string,
  userName: string,
  artistName: string,
  originalSubject: string,
  responseText: string,
  artistProfileUrl: string
) {
  return invokeEmailFunction({
    type: 'inquiry_response',
    to,
    data: { userName, artistName, originalSubject, responseText, artistProfileUrl },
  });
}

/** Send an order confirmation email after successful Stripe checkout */
export async function sendOrderConfirmationEmail(
  to: string,
  plan: string,
  subscriptionId: string,
  billingPeriodEnd: string,
  amount: string,
  currency: string,
  dashboardUrl: string,
  userName?: string
) {
  return invokeEmailFunction({
    type: 'order_confirmation',
    to,
    data: { userName, plan, subscriptionId, billingPeriodEnd, amount, currency, dashboardUrl },
  });
}
